import { OrgTree, Position, OrgBoardBuilderService } from '../../services/org-board-builder.service';
import { SearchableSelectComponent } from '../../../searchable-select/searchable-select.component';
import { deepCopy, DisplayValue, ECONode, isObjectEmpty, Organization } from '@newgenus/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { OrgCreateNewPositionDialogComponent } from './org-create-position.dialog';
import { OrgBoardService } from '../../services/org-board-service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from '../../../material.module';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { JsonPipe, NgStyle } from '@angular/common';

@Component({
    selector: 'shared-org-position-dialog',
    imports: [
        MaterialModule,
        NgStyle,
        ReactiveFormsModule,
        FormsModule,
        OrgCreateNewPositionDialogComponent,
        SearchableSelectComponent,
        JsonPipe,
    ],
    templateUrl: './org-position-dialog.html',
    styleUrls: ['./org-position-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgPositionDialogComponent implements OnInit, OnDestroy {
    //#region Variables
    // Variables for this component that are within scope of "this" component.

    public positionControl = new FormControl('');

    public orgTree: OrgTree | undefined;
    public positions: Position[] = [];
    public selectedSecurityGroups: any;
    public org!: Organization;
    public selectedPosition: any | undefined;
    public selectedPositions: any[] | undefined;
    public searchablePositions: DisplayValue[] = [];

    /**
     * Whether the expand nodes callback function should process the event or not.
     */
    private expandedNodesDisabled = false;

    /**
     * An array containing the nodes tsecurityGroup have been expanded.
     */
    private expandedNodesForTree5: ExpandedNode[] = [];

    public treeControlSecurityGroups = new FlatTreeControl<OrgNodeChild>(
        (node) => node.level,
        (node) => node.expandable
    );

    public treeFlattenerSecurityGroups = new MatTreeFlattener(
        this._transformerSecurityGroups,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    public dataSource = new MatTreeFlatDataSource(this.treeControlSecurityGroups, this.treeFlattenerSecurityGroups);

    public orgKey!: string;
    public showLoader = false;
    private destroy$ = new Subject<void>();

    public dialog = inject(MatDialog);
    public snackbar = inject(MatSnackBar);
    public obService = inject(OrgBoardService);
    public orgBuilderService = inject(OrgBoardBuilderService);

    //#endregion

    constructor(
        public dialogRef: MatDialogRef<OrgPositionDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
            node: ECONode<any>;
            selectedPositionKeys?: Array<string>;
            partTypes: Array<string>;
            config: {
                selection: 'single' | 'multiple';
                allowCreate: boolean;
            };
        }
    ) {}

    //#region Instantiation
    // Setup\fetches\initialization.

    public ngOnInit(): void {
        // console.log('OrgPositionDialogComponent > ngOnInit > this.data:', this.data);
        this.updateOrgPositionTree();

        if (this.data.selectedPositionKeys) {
            // Find selected parts in tree data.
            // console.log('ngOnInit > this.data.selectedPositionKeys:', this.data.selectedPositionKeys);
            this.data.selectedPositionKeys.forEach((partKey) => {
                const match = this.dataSource.data.find((node: any) => {
                    return node?.data?.partKey === partKey || node?.node?.data?.partKey === partKey;
                });
                // console.log('ngOnInit > selectedParts > partKey:', partKey);
                // console.log('ngOnInit > Match:', match);
            });
        }

        this.setSearchablePositions();

        this.obService.organization$.pipe(debounceTime(2000), takeUntil(this.destroy$)).subscribe((org) => {
            this.data.organization = org;
            this.updateOrgPositionTree();
            this.setSearchablePositions();
        });
    }

    private updateOrgPositionTree() {
        this.orgTree = this.orgBuilderService.buildOrg(this.data.organization, this.data.organization.key);
        this.positions = this.orgTree.positions;
        this.org = this.data.organization;

        setTimeout(() => {
            this.onSelectInitialPart();
        });
    }

    //#endregion
    //#region User Interactions
    // Callbacks from html, anything the user can "interact" with from the view.

    public async onSubmit() {
        if (!this.selectedPosition && !this.selectedPositions) {
            this.snackbar.open('Please select an org part.', 'OK', { duration: 3000 });
            return;
        }

        if (this.data.config.selection === 'multiple') {
            // console.log('Selected positions multiple:', this.selectedPositions);
            this.dialogRef.close(this.selectedPositions);
        } else {
            // console.log('Selected position:', this.selectedPosition);
            this.dialogRef.close(this.selectedPosition);
        }
    }

    toggleCheckbox() {
        // toggle existing positions on hat in the tree to true

        // console.log('toggleCheckbox > selectedPositions 1:', this.selectedPositions);
        // console.log('toggleCheckbox > selectedPositionKeys:', this.data.selectedPositionKeys);
        // console.log('toggleCheckbox > dataSource:', this.dataSource.data);

        // if(this.data.selectedPositionKeys) {
        //     this.selectedPositions = this.data.selectedPositionKeys;
        // }
        if (!this.selectedPositions) {
            this.selectedPositions = [];
        }

        // this.data.selectedPositionKeys?.forEach((pos: any) => {
        //     console.log('toggleCheckbox > pos:', pos);
        //     this.dataSource.data.forEach((data: any) => {
        //         console.log("data.data.partKey", data.data.partKey, "pos.partKey", pos.partKey);
        //         if (data.data.partKey === pos.partKey) {
        //             console.log('toggleCheckbox > data: being checked');
        //             data.isChecked = true;
        //         }
        //         console.log('toggleCheckbox > data:', data);
        //     });
        // });
        // this.isChecked = !this.isChecked;

        // Recursive function to traverse the tree and find a match
        const checkNode = (node: any, pos: any) => {
            if (node.data.partKey === pos.partKey) {
                // console.log('toggleCheckbox > data: being checked');
                node.data.isChecked = true;
                if (this.selectedPositions) {
                    this.selectedPositions.push(node);
                }
            }
            if (node.children) {
                node.children.forEach((child: any) => checkNode(child, pos));
            }
        };

        // Iterate over selectedPositionKeys and update the tree
        this.data.selectedPositionKeys?.forEach((pos: any) => {
            // console.log('toggleCheckbox > pos:', pos);
            this.dataSource.data.forEach((data: any) => {
                checkNode(data, pos);
            });
        });

        // console.log('toggleCheckbox > selectedPositions 2:', this.selectedPositions);
        // console.log('toggleCheckbox > this.dataSource:', this.dataSource.data);
    }

    public onCheckNode(node: any, event: MatCheckboxChange) {
        if (!this.selectedPositions) {
            this.selectedPositions = [];
        }

        if (event.checked) {
            this.selectedPositions.push(node.node);
            // console.log("pushing node : ", node);
            // console.log("pushing node to this.selectedPositions", this.selectedPositions);
        } else {
            // console.log("removing node from this.selectedPositions 1:", this.selectedPositions);
            // console.log("removing node : ", node);
            const index = this.selectedPositions.findIndex((pos) => pos.data.partKey === node.node.data.partKey);
            // console.log("index", index);
            if (index > -1) {
                this.selectedPositions.splice(index, 1);
                // console.log("removing node from this.selectedPositions 2:", this.selectedPositions);
            }
        }
    }

    public onClear() {
        this.positionControl.setValue('');
        this.onSelectInitialPart();
    }

    public createNewPosition(node: any, type: string): void {
        let siblings: any[] = [],
            children: any[] = [];
        if (type === 'sibling') {
            siblings = this.findPartSiblings(node);
        } else if (type === 'child') {
            children = node.node?.children || [];
        }

        const dialog = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '65%',
            height: '80%',
            minHeight: '300px',
            data: {
                organization: this.org,
                type: type,
                node: node,
                partTypes: this.data.partTypes,
                siblings: JSON.parse(JSON.stringify(siblings)),
                children: JSON.parse(JSON.stringify(children)),
            },
        });

        dialog.afterClosed().subscribe(async (result) => {
            if (result && result.bulkTree) {
                // ── Bulk Builder result ──
                this.showLoader = true;
                node.showLoader = true;

                try {
                    const anchorPartKey = result.anchorPartKey || node?.node?.data?.partKey;
                    const orgKey = this.data.organization?.key;
                    const graftPoints = result.graftPoints || [];
                    await this.obService.bulkAddParts(
                        orgKey,
                        anchorPartKey,
                        result.insertionType || (type as 'child' | 'sibling'),
                        result.bulkTree,
                        result.siblingOrder || [],
                        graftPoints
                    );
                    let totalPositions = (result.bulkTree || []).length;
                    for (const graft of graftPoints) {
                        totalPositions += graft.children.length;
                    }
                    this.snackbar.open(`Bulk creation submitted (${totalPositions} org parts). Processing...`, 'OK', {
                        duration: 4000,
                    });
                } catch (error) {
                    console.error('Bulk add failed:', error);
                    this.snackbar.open('Bulk creation failed. See console for details.', 'OK', { duration: 5000 });
                }

                this.showLoader = false;
                node.showLoader = false;
            } else if (result && result.newPart) {
                this.showLoader = true; // Show loader line loader between tree and header.
                node.showLoader = true; // Show spinner after add button.

                // Determine if the Order has changed.
                let isOrderUnchanged = true;
                const originalArray = type === 'sibling' ? siblings : children;
                // Iterate over siblings/children as they will exclude the new part if there is one (New parts can be positioned at the end of the list and therefore should be excluded from this check).
                isOrderUnchanged = originalArray.every((siblingOrChild) => {
                    const re = result.orderOfAppearance.some((part: any) => {
                        // Determine if the position for the provided sibling/child is still the same as the order of appearance from the result set.
                        return (
                            part.data.partKey === siblingOrChild.data.partKey &&
                            part.position === siblingOrChild.position
                        );
                    });

                    return re;
                });

                // Set ID on part, this is based on the highest ID in the list +1.
                const orgParts = this.data.organization?.orgParts || [];
                result.newPart.id = (Math.max(...orgParts.map((part) => part.id)) || 0) + 1;

                // Save only the new part.
                await this.obService.saveNewPart(result.newPart);

                // Check if the other parts will need to be updated as well.
                if (!isOrderUnchanged) {
                    const partsToUpdate = result.orderOfAppearance
                        .filter((part: any) => part.data.partKey !== result.newPart.partKey)
                        .map((part: any) => part.data);
                    await this.obService.updateParts(partsToUpdate);
                }

                this.showLoader = false;
                node.showLoader = false;
                // TODO show some kind of greyed-out part in the tree until the task queue is done?
            }
        });
    }

    public expandAll() {
        this.treeControlSecurityGroups.dataNodes.forEach((dataEntry) =>
            this.treeControlSecurityGroups.expand(dataEntry)
        );
    }

    public collapseAll() {
        this.treeControlSecurityGroups.dataNodes.forEach((dataEntry) =>
            this.treeControlSecurityGroups.collapse(dataEntry)
        );
    }
    //#endregion
    //#region Events and Domain Functions
    // $watches/$on events, functions which don't fall into any of the other regions,
    // including any function that has to do domain functionality.

    /**
     * Add or remove the node from the expanded nodes list.
     */
    public onToggleNodeExpand(nodeChild: OrgNodeChild, isExpanded: boolean): void {
        if (this.expandedNodesDisabled) return;

        const arrRef: Array<ExpandedNode> = this.expandedNodesForTree5;

        // Remove node if already in expandedNodes and isExpanded is false.
        if (!isExpanded && arrRef.some((node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey)) {
            // Remove node if already in expandedNodes by slice;
            const index = arrRef.findIndex(
                (node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey
            );
            arrRef.splice(index, 1);
        }

        // Add Node if not in expandedNodes.
        else if (
            isExpanded &&
            !arrRef.some((node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey)
        ) {
            // Push node to expandedNodes.
            arrRef.push({
                level: nodeChild.level,
                // id: nodeChild.key + treeId,
                id: nodeChild.expandedKey,
                node: nodeChild,
            });
        }
        // Sort expandedNodes by level.
        arrRef.sort((a, b) => a.level - b.level);
    }

    public onSelectPosition() {
        // TODO this can be used to action something after selecting a position, like an instant close of the dialog.
        // console.log('Selected position:', this.selectedPosition);
    }

    public onSelectInitialPart(selectedPartKey?: string): void {
        // const securityGroup = securityGroupKey;
        this.dataSource = new MatTreeFlatDataSource(
            this.treeControlSecurityGroups,
            this.treeFlattenerSecurityGroups as any
        );
        let unFlattened = this.orgBuilderService.buildPositionsTreeFromPart(this.org, selectedPartKey);

        // Empty object would mean there are no positions in the organization.
        // Create a default object to display in the tree instructing the user to create a position.
        if (isObjectEmpty(unFlattened)) {
            console.log('No data to display');
            unFlattened = {
                selected: true,
                name: 'Create the first org part by clicking on plus:',
                color: 'black',
                background: 'white',
                data: {
                    name: 'Create the first org part by clicking on plus:',
                },
                children: [],
            };
        }

        this.dataSource.data = [unFlattened];
        this.expandPreviouslyExpandedNodes();

        this.expandAll();
        this.toggleCheckbox();
    }

    //#endregion
    //#region Data Access & Subscriptions
    // API interactions, NGRX selects, etc.

    //#endregion
    //#region Setters, Updaters and Preloaders
    // Setter functions, and methods called from the Instantiation region.

    private setSearchablePositions() {
        const partsCopy = deepCopy(this.data.organization?.orgParts || []);
        this.searchablePositions = partsCopy
            // Sort by depth, lowest to highest.
            .sort((a, b) => a.depth - b.depth)
            .map((part) => ({
                display: part.name,
                value: part.partKey,
            }));
    }

    /**
     * Expand all nodes tsecurityGroup have been expanded before, using the ```expandedNodes``` array.
     */
    private expandPreviouslyExpandedNodes() {
        const treeControl: FlatTreeControl<OrgNodeChild> = this.treeControlSecurityGroups;
        const expandedNodes: ExpandedNode[] = this.expandedNodesForTree5;

        expandedNodes.forEach((prevExpanded) => {
            const dataEntry = treeControl.dataNodes.find(
                (d: any) => d.expandedKey === prevExpanded.node.expandedKey && d.level === prevExpanded.node.level
            );
            if (dataEntry) {
                treeControl.expand(dataEntry);
            }
        });
    }

    private findPartSiblings(mainNode: OrgNodeChild): any[] {
        const parentKey: string | undefined = mainNode?.node?.data?.parentPartKey;

        if (!parentKey) return [];

        // function recursivelyFlattenArray(node: any, arr: any[]): any[] {
        //     arr.push(node);

        //     if (node.children)
        //         node.children.forEach((child: any) => recursivelyFlattenArray(child, arr));

        //     return arr;
        // }
        // const flattenedChildren = recursivelyFlattenArray(this.dataSource.data[0], [])
        const flattenedChildren = this.orgBuilderService.flattenPositions(this.dataSource.data[0] as any);

        const results: any[] = [];
        flattenedChildren.forEach((node) => {
            if (node.data.parentPartKey === parentKey) results.push(node);
        });

        return results;
    }

    //#endregion
    //#region Getters and Filters
    // Getter functions and filter methods/functions.

    //#endregion
    //#region Change Detection
    // Deep config comparisons, change log generation, hasChangesFn, etc.

    //#endregion
    //#region Parsing and Validation
    // Transformation/mapping methods
    // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

    public hasChild = (_: number, node: OrgNodeChild) => node.expandable;

    private _transformerSecurityGroups(node: OrgNode, level: number) {
        return {
            node,
            expandable: !!node.children && node.children.length > 0,
            name: node?.data?.name,
            securityGroupName: node?.data?.securityGroupName,
            userName: node?.data?.userName || '',
            multiUsers: node?.data?.multiUsers,
            inherited: node?.data?.inherited,
            hasPermissions: node?.data?.hasPermissions,
            key: node?.data?.key,
            expandedKey: node?.data?.expandedKey,
            selected: node?.selected,
            isChecked: node?.data?.isChecked,
            type: node?.data?.type,
            color: node.color,
            background: node.background,
            level: level,
            corruptedData: node?.data?.corruptedData,
        } as OrgNodeChild;
    }

    //#endregion
    //#region Response Handlers
    // API/NGRX callback handlers.

    //#endregion
    //#region Navigation
    // Confirmation close dialogs, router guards, etc.

    //#endregion
    //#region Post Instantiation
    // Post-init fetch requests, used in such as landing page, after view init, etc.

    //#endregion

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface ExpandedNode {
    id: string;
    level: number;
    node: any;
}

interface OrgNodeChild {
    background: string;
    color: string;
    expandable: boolean;
    securityGroupName?: string;
    key: string;
    expandedKey: string;
    level: number;
    name: string;
    selected: boolean;
    isChecked?: boolean;
    type: string;
    userName: string;
    multiUsers?: boolean;
    inherited?: boolean;
    hasPermissions?: boolean;
    users?: [];
    node: any;
}

interface OrgNode {
    name?: string;
    color?: string;
    selected?: boolean;
    background?: string;
    data?: {
        name: string;
        securityGroupName?: string;
        userName?: string;
        multiUsers?: boolean;
        inherited?: boolean;
        hasPermissions?: boolean;
        type: string;
        user?: { name: string; key?: string };
        key?: string;
        expandedKey?: string;
        corruptedData?: boolean;
        users: [];
        isChecked?: boolean;
    };
    children?: OrgNode[];
}
